import { FC, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { RebillLocationsInfoSchema, RebillLocationsInfo } from './schemas';
import { GraniteInput } from 'components/V2/Input/GraniteInput';
import { GraniteButton } from 'components/V2/Button/GraniteButton';
import { GraniteSelect, OptionType } from 'components/Select/Select';
import PhoneNumberInput from 'components/V2/Input/PhoneNumber';
import FormSection from 'components/FormSection';
import Divider from 'components/Divider/Divider';
import AddressInput from 'screens/InvoiceAnalyzer/AddressInput';
import { InformationCircle } from 'react-ionicons';
import {
  billinTypeOptions,
  invoiceDeliveryOptions,
} from 'shared/constants/options';
import { getInvoiceStyleOptions } from 'screens/LetUsHelp/GetStarted/CreateChildAccount/utils';
import { SiteAddress } from 'api/addresssearch/schema';
import { useQuery } from 'react-query';
import { getAccessParentAccounts } from 'api/accessexpressv3/api';

interface LocationInfoFormProps {
  cwLocations: (SiteAddress & { account_name: string; site_name: string })[];
  location: {
    address1: string;
    address2?: string | null;
    city: string;
    state: string;
    zip_code: string;
    id?: string;
  } | null;
  onNext: (data: RebillLocationsInfo) => void;
  onCancel: () => void;
  initialData?: RebillLocationsInfo;
  showCreateAccountFields: boolean;
  setShowCreateAccountFields: React.Dispatch<React.SetStateAction<boolean>>;
  selectedOption: OptionType | null;
  setSelectedOption: React.Dispatch<React.SetStateAction<OptionType | null>>;
}

const LocationInfoForm: FC<LocationInfoFormProps> = ({
  cwLocations,
  location,
  onNext,
  onCancel,
  initialData,
  showCreateAccountFields,
  setShowCreateAccountFields,
  selectedOption,
  setSelectedOption,
}) => {
  const [parentAccountOptions, setParentAccountOptions] = useState<
    { label: string; value: string; subvalue?: string }[]
  >([]);

  const {
    handleSubmit,
    control,
    register,
    setValue,
    resetField,
    formState: { errors },
    watch,
  } = useForm<RebillLocationsInfo>({
    resolver: zodResolver(RebillLocationsInfoSchema),
    defaultValues: {
      ...initialData,
      create_child_account: false,
    },
  });

  const onSubmit = (data: RebillLocationsInfo): void => {
    onNext(data);
  };

  const onCreateNewAccount = () => {
    setShowCreateAccountFields(true);
    setValue('create_child_account', true, { shouldValidate: true });
  };

  const currentMacnum = watch('site.macnum');
  const currentCity = watch('site.city');
  const currentBillingType = watch('billing_type');

  const shouldFetchAccounts =
    (!currentMacnum && !!currentCity) || showCreateAccountFields;

  useQuery(
    ['access-parent-accounts', currentCity, currentMacnum],
    () => getAccessParentAccounts(),
    {
      enabled: shouldFetchAccounts,
      staleTime: Infinity,
      onSuccess: (data) => {
        const formatted = data.map((item) => {
          const parts = item.name
            ? item.name.split('-').map((part) => part.trim())
            : ['', ''];
          return { label: parts[0], value: parts[0], subvalue: parts[1] };
        });

        setParentAccountOptions((prev) =>
          JSON.stringify(prev) === JSON.stringify(formatted) ? prev : formatted,
        );
      },
    },
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-8 p-8">
      <AddressInput
        locations={cwLocations ?? []}
        location={location}
        onLocationSelected={(selected) => {
          if (selected) setValue('site', selected);
          else resetField('site');
        }}
        onCreateNewAccount={onCreateNewAccount}
        selectedOption={selectedOption}
        setSelectedOption={setSelectedOption}
      />

      {(!currentMacnum || showCreateAccountFields) && (
        <>
          {!showCreateAccountFields && (
            <div className="flex items-center gap-2 rounded bg-[#9796F314] p-4 ring-1 ring-purple-300">
              <InformationCircle color="rgba(151,150,243,1)" />
              <p className="font-bold text-content-base-default">
                This address is not associated with an existing account. Create
                a new account by completing the below fields.
              </p>
            </div>
          )}

          <FormSection gridClassName="grid-cols-2" className="w-full">
            <GraniteInput
              label="Location name"
              {...register('location_name')}
              error={errors.location_name?.message}
            />
            <Controller
              name="parent_account"
              control={control}
              render={({ field }) => (
                <GraniteSelect
                  {...field}
                  options={parentAccountOptions}
                  label="Parent account"
                  error={errors.parent_account?.message}
                />
              )}
            />
          </FormSection>

          <FormSection gridClassName="grid-cols-3" className="w-full">
            <Controller
              name="billing_type"
              control={control}
              render={({ field }) => (
                <GraniteSelect
                  {...field}
                  options={billinTypeOptions}
                  label="Billing type"
                  onChange={(opt: OptionType | null) => {
                    field.onChange(opt);
                    if (opt) resetField('invoice_style');
                  }}
                  error={errors.billing_type?.message}
                />
              )}
            />
            <Controller
              name="invoice_style"
              control={control}
              render={({ field }) => (
                <GraniteSelect
                  {...field}
                  options={
                    currentBillingType
                      ? getInvoiceStyleOptions(currentBillingType)
                      : []
                  }
                  label="Invoice style"
                  error={errors.invoice_style?.message}
                />
              )}
            />
            <Controller
              name="invoice_delivery_method"
              control={control}
              render={({ field }) => (
                <GraniteSelect
                  {...field}
                  options={invoiceDeliveryOptions}
                  label="Invoice delivery method"
                  error={errors.invoice_delivery_method?.message}
                />
              )}
            />
          </FormSection>
        </>
      )}

      <Divider layout="horizontal" variant="subdued" />

      <FormSection gridClassName="grid-cols-2" title="Location details">
        <GraniteInput
          {...register('business_name')}
          label="Business name"
          error={errors.business_name?.message}
        />
        <GraniteInput
          label="Suite/unit/flr # (optional)"
          {...register('unit')}
          error={errors.unit?.message}
        />
      </FormSection>

      <Divider layout="horizontal" variant="subdued" />

      <FormSection gridClassName="grid-cols-2" title="Location contact">
        <GraniteInput
          {...register('name')}
          label="Name"
          error={errors.name?.message}
        />
        <PhoneNumberInput
          name="phone_number"
          control={control}
          label="Phone number"
          error={errors.phone_number?.message}
        />
      </FormSection>

      <div className="mt-4 flex gap-5">
        <GraniteButton
          size="large"
          onClick={onCancel}
          variant="secondary"
          type="button"
        >
          Cancel
        </GraniteButton>
        <GraniteButton type="submit" size="large" variant="primary">
          Next
        </GraniteButton>
      </div>
    </form>
  );
};

export default LocationInfoForm;
